import React from "react";

const Article = () => {
  return (
    <div className="mb-20">
      <div>
        <div>
          <h2 id="spac_s">SpaC</h2>
          <hr className="dividergeneral mb-5"></hr>

          <p className="mb-2">
            SpaC is a subunit of the Lactobacillus rhamnosus GG
            sortase-dependent pili, SpaCBA. Its natural function is to adhere
            human mucus and collagen
            <a href="#reference1SpaC">
              <sup>1</sup>
            </a>
            . We use its binding capacities to act as an anchor for our live
            biological therapeutic.
          </p>

          <figure className="flex flex-col justify-center items-center gap-4 mb-10">
            <img
              style={{ width: "60%", height: "60%" }}
              alt="spac"
              id="figure-1-spac"
              src="https://static.igem.wiki/teams/4888/wiki/engineering/spaccrystal-structure.jpeg"
            />
            <figcaption>
              <b>
                <u>Figure 1:</u>{" "}
              </b>
              Crystal structure of pilus adhesin, SpaC from Lactobacillus
              rhamnosus GG, PDB : 6M48
              <a href="#reference2SpaC">
                <sup>2</sup>
              </a>
            </figcaption>
          </figure>

          <p className="mb-2">
            SpaC is composed of five domains, organized as a stalk region and a
            binding region. The stalk region is composed of three domains: D3,
            D4 and D5. The binding region is composed of two domains: D1 and D2,
            with the more outer binding domain, D2, being the one interacting
            with mucus{" "}
            <a href="#reference3SpaC">
              <sup>3</sup>
            </a>
            . It is composed of 836 amino acids and weights 90.86 kDa.
          </p>

          <p className="mb-2">
            The engineering cycle is an iterative process aimed at solving
            engineering problems despite gaps in knowledge. Through iteration of
            this cycle, one will achieve engineering success.
          </p>

          <p className="mb-2">
            In this section we describe how we went through iterations of the
            engineering cycle to equip our live biological therapeutic of an
            anchor. To be an optimal temporary biological anchor, SpaC should
            moor our live biological therapeutic to the intestinal mucus
            temporarily and be present at its surface in a stable configuration.
          </p>

          <p className="mb-2">
            In this section we describe how we went through iterations of the
            engineering cycle to equip our live biotherapeutic product (LBT)
            with an anchor that will bind to mucus in the intestine, so that our
            LBT has time to collect any cadmium present in the gut. To be an
            optimal temporary biological anchor, SpaC should moor our live
            biotherapeutic product to the intestinal mucus temporarily and be
            present at its surface in a stable configuration. Our aim was to
            engineer this biological anchor to have the following criteria:
          </p>
          <ul style={{ listStyle: "inside" }}>
            <li>Mucus mooring</li>

            <li>Stable</li>

            <li>Present at the surface</li>

            <li>Temporary anchoring</li>
          </ul>
          <br />
          <p className="mb-2">
            In the following sections we describe two engineering cycles we went
            through to create our temporary biological anchor. Through our two
            iterations of the engineering cycle, we met all four of our aims and
            successfully equipped our live biotherapeutic product with an
            optimal temporary biological mucus anchor.
          </p>

          <h2 className=" mt-20" id="cycle-1_s">
            Cycle 1
          </h2>
          <hr className="dividergeneral mb-5"></hr>

          <h3 className="mb-5" id="cycle-1-design_s">
            Design
          </h3>

          <p className="mb-2">
            Mucus mooring and stability are intrinsic parameters of a successful
            mucus binding protein. To achieve our first and second requirements,
            to bind mucus and be stable, we did a literature review and chose
            the best suited mucus binding protein for our purposes: SpaC.
          </p>

          <p className="mb-2">
            We then needed to address how we could express SpaC on the bacterias
            surface to meet our third requirement. To mediate its role of
            anchor, SpaC needs to be on the surface of the bacteria. We met this
            requirement by fusing SpaC to the well-known surface display protein
            Lpp-OmpA.{" "}
          </p>

          <p className="mb-2">
            {" "}
            Finally, we addressed our fourth requirement, to mediate temporary
            anchoring. The anchoring of SpaC in the intestine should be only
            temporary to let the live biotherapeutic products be evacuated and
            remove the bound cadmium. Thanks to an expression control system
            composed of an IPTG inducible Lac operator coupled to a theophylline
            riboswitch, SpaC is only produced when the live biotherapeutic
            products is grown in the lab. The IPTG inducible Lac operator system
            allows us to control the transcription and the theophylline
            riboswitch allows us to control the translation. A double system
            allows to counteract the leakiness of the individual systems.
          </p>

          <p className="mb-20">
            Additionally, we had to think about testing requirements. When
            designing our anchor, we designed ways to check that the system
            works as expected. To check that SpaC is indeed expressed, we added
            a Flag tag at its N-terminus. The Flag tag allows to specifically
            retrieve our fusion protein with the appropriate antibody. To check
            that it is on the surface we added a GFP-binding nanobody
            <a href="#reference4SpaC">
              <sup>4</sup>
            </a>{" "}
            at its C-terminus. Indeed, we wanted to have a visual confirmation
            of the surface expression. However, as GFP has a large molecular
            weight (27 kDa), we decided to use a GFP-binding nanobody with the
            small molecular weight of only 12.5 kDa instead of expressing GFP
            fused to SpaC.
          </p>

          <figure className="flex flex-col justify-center items-center gap-4">
            <img
              style={{ width: "60%", height: "60%" }}
              alt="spac"
              id="figure-2-spac"
              src="https://static.igem.wiki/teams/4888/wiki/engineering/spac.png"
            />
            <figcaption>
              <b>
                <u>Figure 2:</u>{" "}
              </b>
              SpaC design
            </figcaption>
          </figure>

          <h3 className="mb-5 mt-10" id="cycle-1-build_s">
            Build
          </h3>

          <p className="mb-2">
            After designing our anchor, we then needed to build it. The
            engineered construct composed of the T7 promoter, the IPTG inducible
            Lac operator, the theophylline riboswitch, the fusion protein:
            Lpp-OmpA+Flag tag+SpaC+GFP-binding nanobody, followed by the LacI
            promoter plus the lac inhibitor gene, was cloned in the pTwist Kan
            High copy vector and synthetized by the Twist Bioscience company
            <a href="#reference5SpaC">
              <sup>5</sup>
            </a>
            , a member of the International Gene Synthesis Consortium. This
            selected vector contains a kanamycin resistance gene for selection
            of carrier bacteria.
          </p>

          <p className="mb-2">
            The resulting plasmid was then transformed into the competent lab
            strain E. Coli BL21 DE3 by our means, resulting in a testable
            engineered biological system.
          </p>

          <h3 className="mb-5 mt-10" id="cycle-1-test_s">
            Test
          </h3>

          <p className="mb-2">
            In this test step of our first engineering cycle, we deciphered if
            our actual anchor design met our aims.
          </p>

          <p className="mb-2">
            We first tested if our fusion protein Lpp-OmpA-SpaC was expressed.
            To do so, we monitored its presence via western blot using an
            anti-Flag antibody.
          </p>
          <p className="mb-2">
            See corresponding experimental setup :
            <a href="../epfl/experiments#Checking-that-SpaC-is-expressed_s">
              {" "}
              Checking that SpaC is expressed{" "}
            </a>
          </p>

          <p className="mb-2">
            At the same time, we investigated the temporary anchoring criterium
            of our design. We tested if the expression control system was
            operable and not leaky. Again, we monitored this thanks to western
            blots. The anchor presence was monitored for four expression
            conditions: IPTG, Theophylline, IPTG + Theophylline, and none.
          </p>
          <p className="mb-2">
            See corresponding experimental setup :
            <a href="../epfl/experiments#theophylline_s">
              {" "}
              Checking that our theophylline-IPTG system works{" "}
            </a>
          </p>

          <p className="mb-2">
            We then tested the stability of SpaC via western blots. We monitored
            the SpaC protein quantity over a few days after expression was
            stopped.{" "}
          </p>
          <p className="mb-2">
            See corresponding experimental setup :
            <a href="../epfl/experiments#Half-life-of-SpaC_s">
              {" "}
              Half life of SpaC
            </a>
          </p>

          <p className="mb-2">
            Next, we tested the surface expression requirement. We used multiple
            assays: a GFP-binding assay, a proteinase K digestion assay, and an
            immunofluorescence assay.{" "}
          </p>
          <p className="mb-2">
            See corresponding experimental setup :
            <a href="../epfl/experiments#Surface_s">
              {" "}
              Checking that SpaC is on the surface{" "}
            </a>
          </p>

          <p className="mb-2">
            In the GFP-binding assay, we used GFP as a SpaC marker. If SpaC was
            expressed on the surface, we intended to observe, via fluorescence
            microscopy, a stronger GFP signal due to the GFP-binding nanobody
            compared to a negative control.{" "}
          </p>

          <p className="mb-2">
            In the proteinase K digestion assay, we used proteinase K digestion
            as the proxy for surface expression. Only proteins facing the
            external environment would be digested. Hence, we expected to see a
            decrease in SpaC quantity if expressed at the surface. The decrease
            was monitored via western blot.
          </p>

          <p className="mb-2">
            In the immunofluorescence assay, we used fluorescent antibodies
            directed towards the Flag-tag as a marker. Under a fluorescence
            microscope, bacteria harboring SpaC at the surface would emit a
            stronger light signal.
          </p>

          <p className="mb-2">
            Finally, we tested the mucus binding criterium. We leveraged mucus
            coated transwells to assess the capacity of our anchor to moor our
            live biotherapeutic product to mucus. The number of bacteria
            attached was monitored via CFU counting. Our LBP was expected to
            form more colonies.
          </p>
          <p className="mb-2">
            See corresponding experimental setup :
            <a href="../epfl/experiments#mucus_s">
              {" "}
              Checking that Our bacteria is able to bind to mucus{" "}
            </a>
          </p>

          <h3 className="mb-5 mt-10" id="cycle-1-learn_s">
            Learn
          </h3>

          <p className="mb-2">
            From the above-mentioned experiments, we observed the correct
            expression and induction profile of our fusion protein, as well as
            anchor stability over multiple days. However, surface expression and
            mucus binding experiments were not conclusive. Taken together, those
            results indicated that our fusion protein is expressed under
            induction, but not transported to the surface where it should be.
          </p>

          <p className="mb-2">
            In our first engineering cycle we achieved stability and temporary
            anchoring, but not surface expression and mucus mooring. To meet the
            last two criteria, we conducted another engineering cycle, described
            in the next section.
          </p>

          <br />

          <h2 className="mt-20" id="cycle-2_s">
            {" "}
            Cycle 2
          </h2>
          <hr className="dividergeneral mb-5"></hr>

          <h3 className="mb-5" id="cycle-2-design_s">
            Design
          </h3>

          <p className="mb-20">
            After some discussion and literature review, we concluded that the
            large size of SpaC might be impairing surface export. The choice
            made in the first design to keep the whole SpaC protein sequence was
            motivated by the fact that its length helps to go deeper into mucus
            and enhance binding. We did not know the size limit for surface
            export, neither the modularity of the binding domains of SpaC.
            Because of this, we decided to design two shorter versions of our
            SpaC fusion protein: one with both the D1 and D2 domains and one
            with the D2 domain only. Those new designs resulted in a reduction
            of 41.03 kDa and 63.35 kDa, respectively. We expected those new
            designs to alleviate the size constraint of surface export.
          </p>

          <div className="flex justify-between">
            <figure className="flex flex-col gap-4 items-center justify-center">
              <img
                src="https://static.igem.wiki/teams/4888/wiki/engineering/d1d2.png"
                alt="d1d2 spac"
                id="figure-3-spac"
              />
              <figcaption>
                <b>
                  <u>Figure 3:</u>{" "}
                </b>
                Spac D1D2 design
              </figcaption>
            </figure>

            <figure className="flex flex-col gap-4 items-center justify-center">
              <img
                src="https://static.igem.wiki/teams/4888/wiki/engineering/d2.png"
                alt="d2 spac"
                id="figure-4-spac"
              />
              <figcaption>
                <b>
                  <u>Figure 4:</u>{" "}
                </b>
                Spac D2 design
              </figcaption>
            </figure>
          </div>
          <h3 className="mb-5 mt-10" id="cycle-2-build_s">
            Build
          </h3>

          <p className="mb-2">
            To shorten our original plasmid, we used two different cloning
            techniques. We used Hifi cloning for the D2 shorter version and KLD
            cloning for the D1 plus D2 shorter version. We transformed our
            shortened plasmids into the competent lab strain E. Coli BL21 DE3,
            resulting in new testable designs of our biological system.
          </p>
          <p className="mb-2">
            See corresponding experimental setup :
            <a href="../epfl/experiments#cloning_s">
              {" "}
              Cloning of SpaC construct{" "}
            </a>
          </p>

          <h3 className="mb-5 mt-10" id="cycle-2-test_s">
            Test
          </h3>

          <p className="mb-2">
            To investigate our two new designs, we first tested surface
            expression to assess if they overcame the surface export constraint
            we observed in our first engineering cycle. We re-performed the
            immunofluorescence assay described in engineering cycle 1 on both
            new designs.
          </p>
          <p className="mb-2">
            See corresponding experimental setup :
            <a href="../epfl/experiments#Immunofluorescence_s">
              {" "}
              Immunofluorescence assay{" "}
            </a>
          </p>

          <p className="mb-2">
            At the same time, we tested the previously met criteria with the
            same experimental setups to ensure that our new designs did not
            alter the stability and temporality of SpaC. We verified that stable
            expression and temporary anchoring were still operational in both of
            our new designs.
          </p>
          <p className="mb-2">
            See corresponding experimental setup :
            <a href="../epfl/experiments#Half-life-of-SpaC_s">
              {" "}
              Half life of SpaC{" "}
            </a>{" "}
            and{" "}
            <a href="../epfl/experiments#theophylline_s">
              {" "}
              Checking that our theophylline-IPTG system works{" "}
            </a>
          </p>

          <p className="mb-2">
            To test the mucus binding criterium, we again tested binding to
            mucus coated transwells with both shortened SpaC versions.
          </p>
          <p className="mb-2">
            See corresponding experimental setup :
            <a href="../epfl/experiments#viability-assay_s">
              {" "}
              Evaluation of Plating Conditions for D2 and D1+D2 Domains in Mucus
              Binding Assays{" "}
            </a>
          </p>

          <h3 className="mb-5 mt-10" id="cycle-2-learn_s">
            Learn
          </h3>

          <p className="mb-2">
            From the latter experiments, we confirmed once more that our
            construct allows the fusion protein expression when induced. Then we
            demonstrated that shortened versions of SpaC make it to the surface
            of the bacteria, hence resolving the size constraint imposed by the
            bacteria surface exporting system. Moreover, we demonstrated that
            surface expressed shortened versions of SpaC mediate bacteria
            mooring to the mucus.
          </p>

          <p className="mb-2">
            After this second engineering cycle, we achieved all four of our
            required criteria for having an optimal temporary biological anchor.
          </p>

          <p className="mb-2">
            Through two iterations of the engineering cycle, we equipped our
            live biotherapeutic product with an optimal temporary biological
            anchor, thereby achieving engineering success.
          </p>
        </div>
        <br />
        <h2 className="mt-20" id="References_s">
          <hr className="dividergeneral mb-5"></hr>
          References
        </h2>
        <ul>
          <ol type="1">
            <li id="reference1SpaC">
              Kankainen, Matti, et al. <br />
              <a href="https://pubmed.ncbi.nlm.nih.gov/19805152/">
                "Comparative genomic analysis of Lactobacillus rhamnosus GG
                reveals pili containing a human-mucus binding protein."
                Proceedings of the National Academy of Sciences 106.40 (2009):
                17193-17198.
              </a>
            </li>
            <li id="reference2SpaC">
              <a href="https://www.rcsb.org/structure/6M48">
                PDB page of the SpaC protein
              </a>
            </li>
            <li id="reference3SpaC">
              Kant, Abhiruchi, et al. <br />
              <a href="https://pubmed.ncbi.nlm.nih.gov/32653644/">
                "Crystal structure of lactobacillar SpaC reveals an atypical
                five-domain pilus tip adhesin: Exposing its substrate-binding
                and assembly in SpaCBA pili." Journal of Structural Biology
                211.3 (2020): 107571.
              </a>
            </li>
            <li id="reference4SpaC">
              Supplementary data of: Glass, David S., and Ingmar H.
              Riedel-Kruse. <br />
              <a href="https://pubmed.ncbi.nlm.nih.gov/30033369/">
                "A synthetic bacterial cell-cell adhesion toolbox for
                programming multicellular morphologies and patterns." Cell 174.3
                (2018): 649-658.
              </a>
            </li>
            <li id="reference5SpaC">
              <a href="https://www.twistbioscience.com/">
                Twist Bioscience web site
              </a>
            </li>
          </ol>
        </ul>
      </div>
    </div>
  );
};

export default Article;
